import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { toApiException } from '@shared/helpers/toApiException';
import { useAbortableAttempt } from '@shared/hooks';
import { GENERIC_ERROR_MESSAGE, SUPPORT_NOTIFIED_MESSAGE } from '@shared/i18n';
import type { TApiException } from '@shared/types';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';
import { useLog } from '@modules/users';

import type { TAiContentSelection, EAiMode } from '../types';

import useRequestAiEdit, { useCancelAiEditRun } from './api';
import type { TAiEditRunResponse } from './types';
import isAiBudgetRefusal from '../helpers/isAiBudgetRefusal.helper';
import toAiErrorMessage from '../helpers/toAiErrorMessage.helper';

export type TAiEditRunStart = {
  aiMode: EAiMode;
  prompt: string;
  selection?: TAiContentSelection;
};

type TParams = {
  onCompleted: (run: TAiEditRunResponse) => Promise<void>;
  onFailed: (message?: string) => void;
};

/** Owns baseline-save, request, polling, cancellation, and terminal settlement for one persisted AI Edit run. */
const useAiEditRun = ({ onCompleted, onFailed }: TParams) => {
  const documentContext = useContext(DocumentContext);
  const logMutation = useLog();
  const requestAiEdit = useRequestAiEdit();
  const cancelAiEditRun = useCancelAiEditRun();
  // Only one run can exist per document - the lock owns that invariant - so a boolean is enough to
  // say a run is active.
  const [isRunActive, setIsRunActive] = useState(false);
  const [activeServerRunId, setActiveServerRunId] = useState<string>();
  const [budgetRefusal, setBudgetRefusal] = useState<TApiException | null>(null);
  const attempt = useAbortableAttempt();
  // Cancel is called from closures opened before the id arrived (the close-confirm dialog),
  // so it must read the current id, not the one captured when the closure was created.
  const activeServerRunIdRef = useRef<string>();
  activeServerRunIdRef.current = activeServerRunId;

  const logFailure = useCallback(
    (code: string, runId?: string) => {
      logMutation.mutate({
        message: `AI_EDIT_RUN_FAILURE code=${code} documentId=${documentContext?.doc._id ?? 'none'} versionId=${documentContext?.versionId ?? 'none'}${runId ? ` runId=${runId}` : ''}`,
      });
    },
    [documentContext?.doc._id, documentContext?.versionId]
  );

  const settleFailure = useCallback(
    (message?: string) => {
      setIsRunActive(false);
      onFailed(message);
    },
    [onFailed]
  );

  const start = useCallback(
    (request: TAiEditRunStart): boolean => {
      if (!documentContext || isRunActive) return false;
      const signal = attempt.begin();
      setIsRunActive(true);

      void (async () => {
        try {
          if (!(await documentContext.save({ forceSave: true }))) {
            if (signal.aborted) return;
            // The save does not say why it failed; a lost connection leaves no log, so this claims no report.
            logFailure('BASELINE_SAVE_FAILED');
            settleFailure(GENERIC_ERROR_MESSAGE);
            return;
          }
          if (signal.aborted) return;

          const serverRun = await requestAiEdit.mutateAsync({
            aiMode: request.aiMode,
            documentId: documentContext.doc._id,
            prompt: request.prompt,
            contentSelection: request.selection,
          });
          // Cancelled while the create request was in flight: stop the run now that its id is known.
          if (signal.aborted) {
            cancelAiEditRun.mutate(serverRun.runId);
            return;
          }
          requestAiEdit.watch(serverRun);
          setActiveServerRunId(serverRun.runId);
        } catch (error) {
          // A cancelled attempt has nothing left to report; settling here would clear the newer one.
          if (signal.aborted) return;
          const apiError = toApiException(error);
          logFailure(apiError?.id || (error instanceof Error ? error.message : 'UNKNOWN'));
          if (isAiBudgetRefusal(apiError)) {
            setBudgetRefusal(apiError);
            settleFailure();
            return;
          }
          settleFailure(toAiErrorMessage(error));
        }
      })();

      return true;
    },
    [documentContext, isRunActive, logFailure, settleFailure]
  );

  const cancel = useCallback(() => {
    // Tell the backend to stop the generating run - the local reset alone would leave it
    // running server-side until the stale-run sweep fails it.
    attempt.abort();
    if (activeServerRunIdRef.current) cancelAiEditRun.mutate(activeServerRunIdRef.current);
    setIsRunActive(false);
    setActiveServerRunId(undefined);
    requestAiEdit.reset();
  }, []);

  useEffect(() => {
    const serverRun = requestAiEdit.run;
    if (!activeServerRunId || serverRun?.runId !== activeServerRunId) return;
    if (serverRun.status === EBulkOperationStatus.CREATED) return;

    setActiveServerRunId(undefined);
    requestAiEdit.reset();
    if (!isRunActive) return;

    if (serverRun.status === EBulkOperationStatus.COMPLETED) {
      void (async () => {
        try {
          await onCompleted(serverRun);
          setIsRunActive(false);
        } catch (error) {
          logFailure(error instanceof Error ? error.message : 'UNKNOWN', serverRun.runId);
          settleFailure(SUPPORT_NOTIFIED_MESSAGE);
        }
      })();
      return;
    }

    logFailure('SERVER_FAILED', serverRun.runId);
    settleFailure(serverRun.errorMessage || SUPPORT_NOTIFIED_MESSAGE);
  }, [isRunActive, activeServerRunId, logFailure, onCompleted, requestAiEdit.run, settleFailure]);

  useEffect(() => {
    if (!activeServerRunId || !requestAiEdit.pollingError) return;

    setActiveServerRunId(undefined);
    requestAiEdit.reset();
    if (isRunActive) settleFailure(toAiErrorMessage(requestAiEdit.pollingError));
  }, [isRunActive, activeServerRunId, requestAiEdit.pollingError, settleFailure]);

  const currentServerRun = requestAiEdit.run;
  const serverProgressMessage =
    currentServerRun?.runId === activeServerRunId ? currentServerRun?.progressMessage : undefined;
  const progressMessage = serverProgressMessage || 'Preparing document changes...';

  return {
    isPending: isRunActive || requestAiEdit.isPending,
    progressMessage,
    budgetRefusal,
    start,
    cancel,
  };
};

export default useAiEditRun;
